/* PWA install: capture beforeinstallprompt ASAP (imported first in index.js). */
import { useCallback, useEffect, useState } from "react";

let deferredPrompt = null;
const listeners = new Set();

const notify = (available) => listeners.forEach((fn) => fn(available));

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify(true);
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify(false);
  });
}

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export const isIos = () => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
};

export const usePwaInstall = () => {
  const [installable, setInstallable] = useState(() => !!deferredPrompt && !isStandalone());
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const fn = (available) => setInstallable(available && !isStandalone());
    listeners.add(fn);
    const onInstalled = () => {
      setInstalled(true);
      setInstallable(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      listeners.delete(fn);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = deferredPrompt;
    if (!evt) return null;
    evt.prompt();
    let outcome = null;
    try {
      const choice = await evt.userChoice;
      outcome = choice?.outcome || null;
    } catch (e) {
      outcome = null;
    }
    deferredPrompt = null;
    notify(false);
    return outcome;
  }, []);

  return {
    installable: installable && !installed,
    installed,
    showIosHelp: !installed && !installable && isIos(),
    promptInstall,
  };
};
